import React, {
  KeyboardEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { routeGroups, RoutePaths } from "../../enumStrings";
import type { IndividualRoute } from "../../enumStrings";
import { UserType } from "../../graphql/types";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faDesktop,
  faChartPie,
  faCalculator,
  faUsers,
  faUserPlus,
  faClipboardCheck,
  faRightFromBracket,
  faChevronDown,
  faChevronRight,
  faTimes,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCalendar,
  faEnvelope,
  faFileLines,
} from "@fortawesome/free-regular-svg-icons";
import cmpLogo from "../../images/cmp_logo.png";
import cpaLogo from "../../images/cpa_logo.png";
import useGetChamberFromUserIdInAuth from "../../hooks/useGetChamberFromUserIdInAuth";
import { addHttpToUrl } from "../../utils/addHttpToUrl";
import { useHandleLogout } from "../../hooks/useHandleLogout";

type ChamberAdminRoute = IndividualRoute<UserType.ChamberAdmin>;
type NavIcon = IconDefinition | "cpa_logo";

type NavLinkConfig = {
  path: RoutePaths;
  title: string;
  isExternalLink?: boolean;
  isPrimary?: boolean;
  detail?: string;
  badge?: string;
  showChevron?: boolean;
};

type NavSectionConfig = {
  id: string;
  title: string;
  icon: NavIcon;
  routes?: NavLinkConfig[];
  route?: NavLinkConfig;
  dividerBefore?: boolean;
};

type FooterNavItem = {
  route: NavLinkConfig;
  icon: NavIcon;
};

type NavbarProps = {
  onClose?: () => void;
};

const getIcon = (iconName?: string): NavIcon => {
  switch (iconName) {
    case "home":
      return faHouse;
    case "person":
      return faUsers;
    case "person_add":
      return faUserPlus;
    case "calendar":
      return faCalendar;
    case "desktop":
      return faDesktop;
    case "email":
      return faEnvelope;
    case "attach_money":
      return faFileLines;
    case "bar_chart":
      return faChartPie;
    case "calculator":
      return faCalculator;
    case "assignment_turned_in":
      return faClipboardCheck;
    case "card_giftcard":
      return "cpa_logo";
    case "question_circle":
      return faCircleQuestion;
    default:
      return faCircleQuestion;
  }
};

const getActiveNestedMenuIds = (
  sections: NavSectionConfig[],
  pathname: string,
) =>
  sections
    .filter((section) =>
      section.routes?.some(
        (route) => !route.isExternalLink && route.path === pathname,
      ),
    )
    .map((section) => section.id);

const getNavTitle = (route: ChamberAdminRoute) =>
  route.navTitle || route.title;

const toNavLinkConfig = (
  route: ChamberAdminRoute,
  isPrimary = false,
): NavLinkConfig => ({
  path: route.path,
  title: getNavTitle(route),
  isExternalLink: route.isExternalLink,
  isPrimary,
  detail: route.navDetail,
  badge: route.navBadge,
});

const shouldShowInMainNav = (route: ChamberAdminRoute) =>
  !route.hideInNavBar && route.navPlacement !== "footer";

const shouldShowInFooterNav = (route: ChamberAdminRoute) =>
  !route.hideInNavBar && route.navPlacement === "footer";

const Navbar: React.FC<NavbarProps> = ({ onClose }) => {
  const location = useLocation();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const handleLogout = useHandleLogout();
  const navItems = routeGroups[UserType.ChamberAdmin];

  const navSections = useMemo<NavSectionConfig[]>(() => {
    return navItems.reduce<NavSectionConfig[]>((sections, item) => {
      if (item.nestedRoute) {
        const routes = item.nestedRoute.routes
          .filter(shouldShowInMainNav)
          .map((route, index) => toNavLinkConfig(route, index === 0));

        if (routes.length === 0) {
          return sections;
        }

        sections.push({
          id: `nested:${item.nestedRoute.title}`,
          title:
            item.nestedRoute.title === "My Chamber"
              ? chamberInfo?.name || item.nestedRoute.title
              : item.nestedRoute.title,
          icon: getIcon(item.nestedRoute.icon),
          routes,
        });

        return sections;
      }

      if (item.route && shouldShowInMainNav(item.route)) {
        sections.push({
          id: `route:${item.route.path}`,
          title: getNavTitle(item.route),
          icon: getIcon(item.route.icon),
          route: toNavLinkConfig(item.route),
          dividerBefore: item.route.navDividerBefore,
        });
      }

      return sections;
    }, []);
  }, [chamberInfo?.name, navItems]);

  const footerNavItems = useMemo<FooterNavItem[]>(() => {
    return navItems.reduce<FooterNavItem[]>((items, item) => {
      if (item.route && shouldShowInFooterNav(item.route)) {
        items.push({
          route: toNavLinkConfig(item.route),
          icon: getIcon(item.route.icon),
        });
      }

      return items;
    }, []);
  }, [navItems]);

  const [openMenus, setOpenMenus] = useState<string[]>(() =>
    getActiveNestedMenuIds(navSections, location.pathname),
  );

  useEffect(() => {
    const activeMenuIds = getActiveNestedMenuIds(navSections, location.pathname);

    if (activeMenuIds.length === 0) {
      return;
    }

    setOpenMenus((currentOpenMenus) => {
      const nextOpenMenus = Array.from(
        new Set([...currentOpenMenus, ...activeMenuIds]),
      );

      return nextOpenMenus.length === currentOpenMenus.length
        ? currentOpenMenus
        : nextOpenMenus;
    });
  }, [location.pathname, navSections]);

  const isRouteActive = (path: RoutePaths, isExternalLink?: boolean) =>
    !isExternalLink && location.pathname === path;

  const isMenuActive = (routes: NavLinkConfig[]) =>
    routes.some((route) => isRouteActive(route.path, route.isExternalLink));

  const toggleMenu = (id: string) => {
    setOpenMenus((currentOpenMenus) =>
      currentOpenMenus.includes(id)
        ? currentOpenMenus.filter((menuId) => menuId !== id)
        : [...currentOpenMenus, id],
    );
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    callback: () => void,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      callback();
    }
  };

  const renderIcon = (icon: NavIcon, isActive = false) => {
    if (icon === "cpa_logo") {
      return (
        <img
          src={cpaLogo}
          alt="Chamber Perks App"
          className="mr-4 h-[22px] w-[22px] shrink-0"
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={icon}
        className={`mr-4 h-[20px] w-[20px] shrink-0 ${
          isActive ? "text-cpaGold" : "text-white"
        }`}
      />
    );
  };

  const getExternalPath = (path: RoutePaths) => {
    if (path === RoutePaths.chamberAdmin_website_builder) {
      const websiteUrl = addHttpToUrl(chamberInfo?.chamberMemberProWebsiteUrl);

      if (!websiteUrl) {
        return null;
      }

      return websiteUrl.endsWith("/")
        ? `${websiteUrl}admin`
        : `${websiteUrl}/admin`;
    }

    return path;
  };

  const renderLinkContent = ({
    icon,
    isActive,
    item,
    showIcon,
  }: {
    icon: NavIcon;
    isActive: boolean;
    item: NavLinkConfig;
    showIcon: boolean;
  }) => (
    <>
      {showIcon && renderIcon(icon, isActive)}
      <span className="min-w-0 flex-1">
        <span className="block">{item.title}</span>
        {item.detail && (
          <span className="block text-[11px] font-bold leading-tight text-white">
            {item.detail}
          </span>
        )}
        {item.badge && (
          <span className="mt-1 inline-flex rounded-full bg-cpaGold px-2.5 py-0.5 text-[10px] font-bold leading-tight text-black">
            {item.badge}
          </span>
        )}
      </span>
      {item.showChevron !== false && (
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`ml-2 h-4 w-4 shrink-0 ${
            isActive ? "text-cpaGold" : "text-white"
          }`}
        />
      )}
    </>
  );

  const renderNavLink = (
    item: NavLinkConfig,
    icon: NavIcon,
    {
      isSubLink = false,
      showIcon = true,
    }: {
      isSubLink?: boolean;
      showIcon?: boolean;
    } = {},
  ) => {
    const isActive = isRouteActive(item.path, item.isExternalLink);
    const linkItem = isSubLink ? { ...item, showChevron: false } : item;
    const isGold = isSubLink ? isActive : isActive || item.isPrimary;
    const textClassName = isGold
      ? "font-bold text-cpaGold"
      : isSubLink
        ? "font-normal text-white"
        : "font-semibold text-white";
    const className = `${
      isSubLink ? "py-2 pl-[56px] pr-5" : "px-5 py-2.5"
    } flex items-center text-[14px] no-underline focus:outline-none ${textClassName} hover:text-cpaGold focus:text-cpaGold`;

    if (linkItem.isExternalLink) {
      const pathToGoTo = getExternalPath(linkItem.path);

      if (!pathToGoTo) {
        return null;
      }

      return (
        <a
          href={pathToGoTo}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {renderLinkContent({ icon, isActive, item: linkItem, showIcon })}
        </a>
      );
    }

    return (
      <Link to={linkItem.path} className={className} tabIndex={0}>
        {renderLinkContent({ icon, isActive, item: linkItem, showIcon })}
      </Link>
    );
  };

  return (
    <div className="relative flex h-screen w-[275px] max-w-[85vw] flex-col overflow-hidden bg-black py-4">
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center text-white hover:text-cpaGold focus:text-cpaGold focus:outline-none md:hidden"
          aria-label="Close navigation"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
      <div className="px-5 pb-5">
        <div className="flex flex-col items-start pr-9 md:pr-0">
          <img
            src={cmpLogo}
            alt="Chamber Member Pro"
            className="w-full max-w-[245px] object-contain"
          />
          <p className="mt-4 text-[15px] font-normal leading-tight text-white">
            Part of One Chamber System™
          </p>
        </div>
      </div>
      <div className="h-px scale-y-50 bg-white" />

      <div className="flex-1 overflow-y-auto py-4">
        {navSections.map((section) => {
          const isOpen = section.routes
            ? openMenus.includes(section.id)
            : false;
          const isActive = section.routes
            ? isMenuActive(section.routes)
            : section.route
              ? isRouteActive(section.route.path, section.route.isExternalLink)
              : false;

          return (
            <React.Fragment key={section.id}>
              {section.dividerBefore && (
                <div className="mx-5 my-3 h-px scale-y-50 bg-white" />
              )}
              {section.routes ? (
                <>
                  <div
                    className={`flex cursor-pointer items-center px-5 py-2.5 text-[14px] font-semibold focus:outline-none hover:text-cpaGold focus:text-cpaGold ${
                      isActive ? "text-cpaGold" : "text-white"
                    }`}
                    onClick={() => toggleMenu(section.id)}
                    aria-label={`Toggle ${section.title} menu`}
                    tabIndex={0}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => toggleMenu(section.id))
                    }
                  >
                    {renderIcon(section.icon, isActive)}
                    <span className="min-w-0 flex-1">{section.title}</span>
                    <FontAwesomeIcon
                      className={`ml-2 h-3.5 w-3.5 shrink-0 ${
                        isActive ? "text-cpaGold" : "text-white"
                      }`}
                      icon={isOpen ? faChevronDown : faChevronRight}
                    />
                  </div>
                  <div className={isOpen ? "block" : "hidden"}>
                    {section.routes.map((route) => (
                      <React.Fragment key={route.path}>
                        {renderNavLink(route, section.icon, {
                          isSubLink: true,
                          showIcon: false,
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </>
              ) : section.route ? (
                renderNavLink(
                  {
                    ...section.route,
                    showChevron: false,
                  },
                  section.icon,
                )
              ) : null}
            </React.Fragment>
          );
        })}
      </div>

      <div className="pt-2 pb-2">
        <div className="mb-2 h-px scale-y-50 bg-white" />
        {footerNavItems.map((footerItem) => (
          <React.Fragment key={footerItem.route.path}>
            {renderNavLink(
              {
                ...footerItem.route,
                showChevron: false,
              },
              footerItem.icon,
            )}
          </React.Fragment>
        ))}
        <button
          type="button"
          className="flex w-full items-center px-5 py-2.5 text-[14px] font-semibold text-cpaGold hover:text-white focus:text-white focus:outline-none"
          onClick={handleLogout}
        >
          <FontAwesomeIcon
            className="mr-4 h-[20px] w-[20px] shrink-0 text-cpaGold"
            icon={faRightFromBracket}
          />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Navbar;
